/**
 * The page's side of the audio core: compile the wasm and decode the Break
 * here on the main thread, hand both to the processor through its options, and
 * pass Cues down and reports back up. Nothing about tempo, Steps or the Grid
 * lives here; the core owns only the frame clock.
 */
import { isManifest, sliceTable } from "./break.ts"

const WASM_URL = "/walker.wasm"
const PROCESSOR_URL = "/walker-processor.js"
const BREAKS_URL = "/breaks/"

/** The Break the Walker plays, by its name in `web/breaks/`. One at a time. */
export const BREAK = "springfield"

/**
 * A sound, as the core numbers it: 0 is a rest, which fires and reports and
 * triggers nothing; 1..5 are the Break's kinds in `KINDS` order (break.ts).
 */
export type Sound = 0 | 1 | 2 | 3 | 4 | 5

/**
 * In generation `gen`, Step `step` sounds `event` at absolute audio frame
 * `frame`, at `velocity`, 0 to 1. The core plays a Slice of that sound at
 * that gain, once, or repeated across `span` as its `forms` say, the repeats
 * softer the more there are.
 */
export interface Cue {
  readonly gen: number
  readonly step: number
  readonly frame: number
  readonly event: Sound
  readonly velocity: number
  /**
   * The Forms, a bitmask as dsp/walker.c defines it: 1 a Ratchet (the Slice
   * twice in the Step), 2 a Roll (four times), both eight. 0 for none.
   */
  readonly forms: number
  /** The Step's length in audio frames, which the repeats are spread across. */
  readonly span: number
}

/** What the core says came out of the speaker, and when. */
export interface Report {
  readonly gen: number
  readonly step: number
  readonly frame: number
  readonly missed: boolean
  /** The report's frame in the context's own time domain. */
  readonly time: number
}

/** Everything the shim posts up, and the only two shapes it posts. */
type FromProcessor = (Report & { readonly t: "report" }) | { readonly t: "error"; readonly message: string }

export interface WalkerAudio {
  readonly context: AudioContext
  readonly cue: (cue: Cue) => void
}

export interface WalkerAudioHandlers {
  readonly onReport?: (report: Report) => void
  /** The worklet threw. It is silent from here on and the page cannot recover it. */
  readonly onError?: (message: string) => void
}

/** The Break as the shim takes it: planar stereo at its own rate, and the Slice table. */
export interface LoadedBreak {
  readonly left: Float32Array
  readonly right: Float32Array
  readonly frames: number
  readonly sampleRate: number
  readonly slices: Int32Array
}

/**
 * Fetches a Break's manifest and audio and decodes the audio at the
 * manifest's rate, the file's own, so nothing is resampled: the browser's own
 * decoder on a lossless file gives the same samples everywhere, and the core
 * interpolates to the device's rate itself.
 */
export const loadBreak = async (name: string): Promise<LoadedBreak> => {
  const response = await fetch(`${BREAKS_URL}${name}.json`)
  if (!response.ok) throw new Error(`break ${name}: ${response.status}`)
  const manifest: unknown = await response.json()
  if (!isManifest(manifest)) throw new Error(`break ${name}: not a manifest`)
  const audio = await fetch(`${BREAKS_URL}${manifest.file}`)
  if (!audio.ok) throw new Error(`break ${name}: ${manifest.file} ${audio.status}`)
  const decoded = await new OfflineAudioContext(2, 1, manifest.sampleRate).decodeAudioData(
    await audio.arrayBuffer()
  )
  // decodeAudioData resamples to the context's rate, so a file at another rate
  // decodes to a different length and its Slices no longer land on its hits.
  const last = manifest.slices.at(-1)
  if (decoded.sampleRate !== manifest.sampleRate || (last !== undefined && last.end > decoded.length)) {
    throw new Error(`break ${name}: ${manifest.file} is not at ${manifest.sampleRate} Hz`)
  }
  const left = decoded.getChannelData(0)
  return {
    left,
    // The core only ever sees stereo.
    right: decoded.numberOfChannels > 1 ? decoded.getChannelData(1) : left,
    frames: decoded.length,
    sampleRate: manifest.sampleRate,
    slices: sliceTable(manifest)
  }
}

/**
 * Starts the audio graph. Call it from a gesture handler: a context created
 * before one starts suspended and `resume` only takes effect inside it.
 */
export const startAudio = async (handlers: WalkerAudioHandlers = {}): Promise<WalkerAudio> => {
  const context = new AudioContext()

  // The worklet scope has no fetch, but a compiled module is serialisable, so
  // it travels through processorOptions and instantiates synchronously there.
  // The Break travels the same way, copied once into the core's memory.
  const [module, , brk] = await Promise.all([
    WebAssembly.compileStreaming(fetch(WASM_URL)),
    context.audioWorklet.addModule(PROCESSOR_URL),
    loadBreak(BREAK)
  ])

  const node = new AudioWorkletNode(context, "walker", {
    numberOfInputs: 0,
    numberOfOutputs: 1,
    outputChannelCount: [2],
    processorOptions: { module, break: brk }
  })

  node.port.onmessage = (event: MessageEvent) => {
    const message = event.data as FromProcessor
    if (message.t === "report") handlers.onReport?.(message)
    else handlers.onError?.(message.message)
  }

  node.connect(context.destination)
  await context.resume()

  return {
    context,
    cue: (cue: Cue) => node.port.postMessage(cue)
  }
}
